import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiImage,
  FiTrash2,
  FiUploadCloud,
  FiX,
  FiEye,
  FiRefreshCw,
} from 'react-icons/fi';
import { API_URL, BASE_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';
import './HeroBackgroundManagement.css';

const publicImageUrl = (filename) => {
  if (!filename) return '';
  const name = String(filename).replace(/^uploads\//, '');
  return `${BASE_URL}/uploads/${name}`;
};

const HeroBackgroundManagement = () => {
  const [images, setImages] = useState([]);
  const [maxCount, setMaxCount] = useState(10);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${getAuthToken()}` };

  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/hero-backgrounds`, { headers: authHeaders });
      setImages(Array.isArray(res.data?.images) ? res.data.images : []);
      setMaxCount(res.data?.max || 10);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load hero backgrounds');
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders.Authorization]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const validateFile = (file) => {
    if (!file) return 'No file selected';
    if (!file.type.startsWith('image/')) return 'Only image files are allowed (JPG, PNG, WebP, GIF)';
    if (file.size > 8 * 1024 * 1024) return 'Image must be smaller than 8 MB';
    return null;
  };

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    if (images.length >= maxCount) {
      toast.error(`Maximum ${maxCount} background images allowed`);
      return;
    }

    setUploading(true);
    setUploadProgress(`Uploading ${file.name}…`);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await axios.post(`${API_URL}/admin/hero-backgrounds`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      setImages(Array.isArray(res.data?.images) ? res.data.images : []);
      toast.success('Background image uploaded — it will appear on the Hero section');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await axios.delete(
        `${API_URL}/admin/hero-backgrounds/${encodeURIComponent(deleteTarget.filename)}`,
        { headers: authHeaders },
      );
      setImages(Array.isArray(res.data?.images) ? res.data.images : []);
      if (preview?.filename === deleteTarget.filename) setPreview(null);
      toast.success('Background image deleted');
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="admin-page hero-bg-page">
      <div className="page-header flex-header">
        <div>
          <h1><FiImage style={{ verticalAlign: 'middle', marginRight: 8 }} />Hero Background Image</h1>
          <p>Upload and manage rotating background images for the website and app Hero section.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={loadImages} disabled={loading}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div className="admin-card hero-bg-upload-card">
        <h3>Upload Background Image</h3>
        <p className="hero-bg-hint">
          Recommended: landscape photos, at least 1200px wide. Images are compressed automatically.
          {' '}({images.length}/{maxCount} used)
        </p>

        <div
          className={`hero-bg-dropzone${uploading ? ' hero-bg-dropzone--busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hero-bg-file-input"
            onChange={handleFileChange}
            disabled={uploading || images.length >= maxCount}
          />
          {uploading ? (
            <>
              <div className="hero-bg-upload-spinner" />
              <p>{uploadProgress || 'Uploading…'}</p>
            </>
          ) : (
            <>
              <FiUploadCloud size={32} className="hero-bg-dropzone-icon" />
              <p><strong>Click to upload</strong> or drag and drop an image here</p>
              <span>JPG, PNG, WebP, GIF · Max 8 MB</span>
            </>
          )}
        </div>
      </div>

      <div className="admin-card hero-bg-gallery-card">
        <h3>Current Background Images</h3>
        {loading ? (
          <div className="skeleton" style={{ height: 200, borderRadius: 8 }} />
        ) : images.length === 0 ? (
          <div className="hero-bg-empty">
            <FiImage size={40} />
            <p>No custom backgrounds yet. Default Hero images are shown until you upload one.</p>
          </div>
        ) : (
          <div className="hero-bg-grid">
            {images.map((img, index) => (
              <article key={img.filename} className="hero-bg-item">
                <div className="hero-bg-item__preview">
                  <img
                    src={publicImageUrl(img.filename)}
                    alt={`Hero background ${index + 1}`}
                    loading="lazy"
                  />
                  <span className="hero-bg-item__order">#{index + 1}</span>
                </div>
                <p className="hero-bg-item__name" title={img.filename}>{img.filename}</p>
                <div className="hero-bg-item__actions">
                  <button
                    type="button"
                    className="btn-secondary hero-bg-btn"
                    onClick={() => setPreview(img)}
                  >
                    <FiEye /> Preview
                  </button>
                  <button
                    type="button"
                    className="btn-secondary hero-bg-btn hero-bg-btn--danger"
                    onClick={() => setDeleteTarget(img)}
                  >
                    <FiTrash2 /> Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {preview && (
        <div className="admin-modal-overlay animate-fade-in" onClick={() => setPreview(null)}>
          <div className="admin-modal hero-bg-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Background Preview</h2>
                <p className="hero-bg-preview-name">{preview.filename}</p>
              </div>
              <button type="button" className="btn-icon" onClick={() => setPreview(null)} aria-label="Close">
                <FiX />
              </button>
            </div>
            <div className="hero-bg-preview-body">
              <img src={publicImageUrl(preview.filename)} alt={preview.filename} />
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="admin-modal-overlay animate-fade-in" onClick={() => !deleteLoading && setDeleteTarget(null)}>
          <div className="admin-modal hero-bg-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Background Image</h2>
              <button
                type="button"
                className="btn-icon"
                onClick={() => !deleteLoading && setDeleteTarget(null)}
                disabled={deleteLoading}
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>
            <div className="modal-form">
              <p className="hero-bg-delete-msg">
                Are you sure you want to delete this hero background image? This cannot be undone.
              </p>
              <p className="hero-bg-delete-file">{deleteTarget.filename}</p>
              <div className="hero-bg-delete-actions">
                <button type="button" className="btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
                  Cancel
                </button>
                <button type="button" className="btn-danger" onClick={confirmDelete} disabled={deleteLoading}>
                  <FiTrash2 /> {deleteLoading ? 'Deleting…' : 'Delete Image'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroBackgroundManagement;
